import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { useApplyTypography } from "@/hooks/useSiteSettings";

/**
 * Public announcement banner — Sprint 36 (Control Center v2).
 *
 * Reads announcement_* keys from the settings hook. Dismissable via
 * localStorage keyed by content hash so re-enabling with new copy
 * resurfaces the banner. Also applies the typography CSS variables
 * as a side-effect (single mount point, no polling).
 */
const TONE_STYLES = {
  info:    "border-intel-200 bg-intel-50 text-intel-900",
  success: "border-emerald-200 bg-emerald-50 text-emerald-900",
  warning: "border-amber-200 bg-amber-50 text-amber-900",
};

const hashOf = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) { h = ((h << 5) - h + s.charCodeAt(i)) | 0; }
  return String(h);
};

export const AnnouncementBanner = () => {
  const settings = useApplyTypography();
  const enabled = settings.announcement_enabled === "true";
  const title = (settings.announcement_title || "").trim();
  const body  = (settings.announcement_body  || "").trim();
  const tone  = settings.announcement_tone || "info";
  const contentHash = hashOf(`${title}|${body}|${tone}`);
  const storageKey = "fursa.announcement.dismissed";

  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const prev = localStorage.getItem(storageKey);
      setDismissed(prev === contentHash);
    } catch { /* ignore */ }
  }, [contentHash]);

  if (!enabled || (!title && !body)) return null;
  if (dismissed) return null;

  const tones = TONE_STYLES[tone] || TONE_STYLES.info;

  return (
    <div
      role="status"
      data-testid="public-announcement-banner"
      className={`w-full border-b ${tones}`}
    >
      <div className="mx-auto flex max-w-7xl items-start justify-between gap-3 px-4 py-2.5 sm:px-6 lg:px-8">
        <div className="min-w-0">
          {title && <p className="text-sm font-semibold leading-tight">{title}</p>}
          {body  && <p className="mt-0.5 text-[12.5px] leading-relaxed opacity-90">{body}</p>}
        </div>
        <button
          type="button"
          onClick={() => {
            try { localStorage.setItem(storageKey, contentHash); } catch { /* ignore */ }
            setDismissed(true);
          }}
          data-testid="public-announcement-dismiss"
          aria-label="إغلاق الإعلان"
          className="mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center rounded-md text-current opacity-70 hover:bg-black/5 hover:opacity-100"
        >
          <X className="h-4 w-4" strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
